export type PalaceKey =
  | 'southeast'
  | 'south'
  | 'southwest'
  | 'east'
  | 'center'
  | 'west'
  | 'northeast'
  | 'north'
  | 'northwest';

export type FlightDirection = 'forward' | 'reverse';
export type MountainYinYang = 'yang' | 'yin';
export type YuanDragon = '天元龙' | '地元龙' | '人元龙';
export type MountainLineStatus = 'pure' | 'replacement' | 'void';
export type VoidLineType = 'none' | 'small' | 'large';

export interface MountainMeta {
  name: string;
  yuanDragon: YuanDragon;
  yinYang: MountainYinYang;
  image: string;
}

export interface MountainLineInfo {
  status: MountainLineStatus;
  statusLabel: string;
  voidLineType: VoidLineType;
  voidLineLabel: string;
  mountainCenter: number;
  offsetFromCenter: number;
  distanceToBoundary: number;
  nearestBoundary: number;
  boundaryPair: string;
  note: string;
}

export interface HouseLuckCell {
  key: PalaceKey;
  label: string;
  trigram: string;
  palaceElement: string;
  periodStar: number;
  periodStarName: string;
  mountainStar: number;
  mountainStarName: string;
  facingStar: number;
  facingStarName: string;
  annualStar: number;
  annualStarName: string;
  x: number;
  y: number;
}

export interface HouseLuckChart {
  houseYear: number;
  annualYear: number;
  period: number;
  periodText: string;
  periodRange: string;
  sitting: PalaceKey;
  facing: PalaceKey;
  sittingLabel: string;
  facingLabel: string;
  sittingMountain: string;
  facingMountain: string;
  sittingMountainLabel: string;
  facingMountainLabel: string;
  sittingMountainMeta: MountainMeta;
  facingMountainMeta: MountainMeta;
  sittingLineInfo: MountainLineInfo;
  facingLineInfo: MountainLineInfo;
  sittingAngle: number;
  facingAngle: number;
  periodCenterStar: number;
  mountainCenterStar: number;
  facingCenterStar: number;
  annualCenterStar: number;
  periodDirection: FlightDirection;
  mountainDirection: FlightDirection;
  facingDirection: FlightDirection;
  annualDirection: FlightDirection;
  flightPath: PalaceKey[];
  cells: HouseLuckCell[];
}

interface PalaceMeta {
  key: PalaceKey;
  label: string;
  trigram: string;
  element: string;
  x: number;
  y: number;
}

export const palaceGrid: PalaceMeta[] = [
  { key: 'southeast', label: '东南', trigram: '巽', element: '木', x: 0, y: 0 },
  { key: 'south', label: '正南', trigram: '离', element: '火', x: 1, y: 0 },
  { key: 'southwest', label: '西南', trigram: '坤', element: '土', x: 2, y: 0 },
  { key: 'east', label: '正东', trigram: '震', element: '木', x: 0, y: 1 },
  { key: 'center', label: '中宫', trigram: '中', element: '土', x: 1, y: 1 },
  { key: 'west', label: '正西', trigram: '兑', element: '金', x: 2, y: 1 },
  { key: 'northeast', label: '东北', trigram: '艮', element: '土', x: 0, y: 2 },
  { key: 'north', label: '正北', trigram: '坎', element: '水', x: 1, y: 2 },
  { key: 'northwest', label: '西北', trigram: '乾', element: '金', x: 2, y: 2 },
];

export const forwardPath: PalaceKey[] = [
  'center',
  'northwest',
  'west',
  'northeast',
  'south',
  'north',
  'southwest',
  'east',
  'southeast',
];

export const oppositePalace: Record<PalaceKey, PalaceKey> = {
  north: 'south',
  south: 'north',
  east: 'west',
  west: 'east',
  northeast: 'southwest',
  southwest: 'northeast',
  northwest: 'southeast',
  southeast: 'northwest',
  center: 'center',
};

export const starNames: Record<number, string> = {
  1: '一白贪狼',
  2: '二黑巨门',
  3: '三碧禄存',
  4: '四绿文曲',
  5: '五黄廉贞',
  6: '六白武曲',
  7: '七赤破军',
  8: '八白左辅',
  9: '九紫右弼',
};

export const chineseNumbers: Record<number, string> = {
  1: '一',
  2: '二',
  3: '三',
  4: '四',
  5: '五',
  6: '六',
  7: '七',
  8: '八',
  9: '九',
};

export const periodRanges: Record<number, string> = {
  1: '1864-1883',
  2: '1884-1903',
  3: '1904-1923',
  4: '1924-1943',
  5: '1944-1963',
  6: '1964-1983',
  7: '1984-2003',
  8: '2004-2023',
  9: '2024-2043',
};

const palaceByKey = new Map(palaceGrid.map((palace) => [palace.key, palace]));

export const wrapStar = (value: number) => {
  const normalized = value % 9;
  return normalized <= 0 ? normalized + 9 : normalized;
};

export const getHousePeriod = (year: number) => {
  const clampedYear = Math.min(Math.max(year, 1864), 2043);
  return Math.floor((clampedYear - 1864) / 20) + 1;
};

export const getAnnualCenterStar = (year: number) => {
  const digitSum = String(year)
    .split('')
    .reduce((sum, digit) => sum + Number(digit), 0);

  return wrapStar(11 - digitSum);
};

export const getPalaceLabel = (palace: PalaceKey) => palaceByKey.get(palace)?.label ?? palace;

export const twentyFourMountains = [
  '子',
  '癸',
  '丑',
  '艮',
  '寅',
  '甲',
  '卯',
  '乙',
  '辰',
  '巽',
  '巳',
  '丙',
  '午',
  '丁',
  '未',
  '坤',
  '申',
  '庚',
  '酉',
  '辛',
  '戌',
  '乾',
  '亥',
  '壬',
] as const;

const mountainMetaByName: Record<typeof twentyFourMountains[number], MountainMeta> = {
  子: { name: '子', yuanDragon: '天元龙', yinYang: 'yin', image: '坎水正位，主智慧、肾水、暗流、桃花与流动之象' },
  癸: { name: '癸', yuanDragon: '人元龙', yinYang: 'yin', image: '阴水余气，主细流、隐伏、谋略、湿寒与暗耗之象' },
  丑: { name: '丑', yuanDragon: '地元龙', yinYang: 'yin', image: '湿土金库，主积蓄、迟滞、脾胃、旧物与库藏之象' },
  艮: { name: '艮', yuanDragon: '天元龙', yinYang: 'yang', image: '少男山止，主少男、门庭、静止、背靠与转折之象' },
  寅: { name: '寅', yuanDragon: '人元龙', yinYang: 'yang', image: '阳木生发，主长进、胆气、动工、文书与新机之象' },
  甲: { name: '甲', yuanDragon: '地元龙', yinYang: 'yang', image: '阳木直达，主长男、事业开创、肝胆、栋梁与挺拔之象' },
  卯: { name: '卯', yuanDragon: '天元龙', yinYang: 'yin', image: '震木正位，主长男、门户、人缘、桃花、声动与伸展之象' },
  乙: { name: '乙', yuanDragon: '人元龙', yinYang: 'yin', image: '阴木柔曲，主花草、文艺、感情、肝木与细致之象' },
  辰: { name: '辰', yuanDragon: '地元龙', yinYang: 'yin', image: '湿土水库，主蓄水、杂气、脾胃、旧事牵连与缓慢之象' },
  巽: { name: '巽', yuanDragon: '天元龙', yinYang: 'yang', image: '长女风木，主文昌、传播、进退、财气入户与风动之象' },
  巳: { name: '巳', yuanDragon: '人元龙', yinYang: 'yang', image: '阴火转阳，主炉灶、心血、口舌、文书、快速变化之象' },
  丙: { name: '丙', yuanDragon: '地元龙', yinYang: 'yang', image: '阳火光明，主名声、眼目、礼仪、热象与显达之象' },
  午: { name: '午', yuanDragon: '天元龙', yinYang: 'yin', image: '离火正位，主名气、心眼、礼法、明堂与火旺之象' },
  丁: { name: '丁', yuanDragon: '人元龙', yinYang: 'yin', image: '阴火灯烛，主细火、文凭、女性、心血与精微之象' },
  未: { name: '未', yuanDragon: '地元龙', yinYang: 'yin', image: '燥土木库，主田宅、脾胃、母系、积累与燥热之象' },
  坤: { name: '坤', yuanDragon: '天元龙', yinYang: 'yang', image: '老母厚土，主女主人、腹部、承载、稳定与众人之象' },
  申: { name: '申', yuanDragon: '人元龙', yinYang: 'yang', image: '阳金发动，主道路、金属、肺骨、变动与权谋之象' },
  庚: { name: '庚', yuanDragon: '地元龙', yinYang: 'yang', image: '阳金刚健，主肃杀、筋骨、决断、刀兵与规则之象' },
  酉: { name: '酉', yuanDragon: '天元龙', yinYang: 'yin', image: '兑金正位，主少女、口舌、悦乐、肺金、桃花与收敛之象' },
  辛: { name: '辛', yuanDragon: '人元龙', yinYang: 'yin', image: '阴金珠玉，主精致、首饰、口舌、肺气与细碎财象' },
  戌: { name: '戌', yuanDragon: '地元龙', yinYang: 'yin', image: '燥土火库，主守成、犬吠、官非、燥热与库藏之象' },
  乾: { name: '乾', yuanDragon: '天元龙', yinYang: 'yang', image: '老父天门，主男主人、头部、权柄、贵人与高处之象' },
  亥: { name: '亥', yuanDragon: '人元龙', yinYang: 'yang', image: '阳水归藏，主远行、暗财、肾水、流动与隐秘之象' },
  壬: { name: '壬', yuanDragon: '地元龙', yinYang: 'yang', image: '阳水大流，主江河、事业流动、智慧、肾水与奔波之象' },
};

export const getTwentyFourMountain = (bearing: number) => {
  const index = Math.round(normalizeDegrees(bearing) / 15) % twentyFourMountains.length;
  return twentyFourMountains[index];
};

export const getMountainMeta = (mountain: string): MountainMeta => (
  mountainMetaByName[mountain as typeof twentyFourMountains[number]] ?? mountainMetaByName.子
);

const guaBoundaryPairs = new Set([
  '癸/丑',
  '寅/甲',
  '乙/辰',
  '巳/丙',
  '丁/未',
  '申/庚',
  '辛/戌',
  '亥/壬',
]);

const signedAngleDiff = (angle: number, center: number) => {
  const diff = normalizeDegrees(angle - center);
  return diff > 180 ? diff - 360 : diff;
};

const getBoundaryPair = (boundaryIndex: number) => {
  const left = twentyFourMountains[(boundaryIndex + twentyFourMountains.length) % twentyFourMountains.length];
  const right = twentyFourMountains[(boundaryIndex + 1) % twentyFourMountains.length];
  return `${left}/${right}`;
};

export const getMountainLineInfo = (bearing: number): MountainLineInfo => {
  const normalizedBearing = normalizeDegrees(bearing);
  const mountainIndex = Math.round(normalizedBearing / 15) % twentyFourMountains.length;
  const mountainCenter = mountainIndex * 15;
  const offsetFromCenter = signedAngleDiff(normalizedBearing, mountainCenter);
  const absOffset = Math.abs(offsetFromCenter);
  const distanceToBoundary = Math.max(0, 7.5 - absOffset);
  const nearestBoundaryIndex = offsetFromCenter >= 0
    ? mountainIndex
    : (mountainIndex - 1 + twentyFourMountains.length) % twentyFourMountains.length;
  const nearestBoundary = normalizeDegrees(nearestBoundaryIndex * 15 + 7.5);
  const boundaryPair = getBoundaryPair(nearestBoundaryIndex);
  const isGuaBoundary = guaBoundaryPairs.has(boundaryPair);
  const status: MountainLineStatus = absOffset <= 4
    ? 'pure'
    : absOffset <= 6
      ? 'replacement'
      : 'void';
  const voidLineType: VoidLineType = status !== 'void'
    ? 'none'
    : isGuaBoundary
      ? 'large'
      : 'small';
  const statusLabel = status === 'pure'
    ? '正向'
    : status === 'replacement'
      ? '兼向'
      : voidLineType === 'large'
        ? '大空亡'
        : '空亡';
  const voidLineLabel = voidLineType === 'large'
    ? '大空亡'
    : voidLineType === 'small'
      ? '小空亡'
      : '非空亡';

  return {
    status,
    statusLabel,
    voidLineType,
    voidLineLabel,
    mountainCenter,
    offsetFromCenter,
    distanceToBoundary,
    nearestBoundary,
    boundaryPair,
    note: status === 'pure'
      ? '坐向落在山中心线左右4度内，气较纯。'
      : status === 'replacement'
        ? '坐向偏离中心线4至6度，属兼向，需按替卦思路审慎判断。'
        : `坐向偏离中心线超过6度，接近${boundaryPair}分界，按${voidLineLabel}论。`,
  };
};

const buildStarMap = (centerStar: number, direction: FlightDirection) => {
  const starByPalace = new Map<PalaceKey, number>();

  forwardPath.forEach((palace, index) => {
    const star = direction === 'forward'
      ? wrapStar(centerStar + index)
      : wrapStar(centerStar - index);
    starByPalace.set(palace, star);
  });

  return starByPalace;
};

const getDirectionalFlight = (mountain: string): FlightDirection => {
  return getMountainMeta(mountain).yinYang === 'yang' ? 'forward' : 'reverse';
};

export const getSittingFacingFromOrientation = (orientation: string) => {
  const map: Record<string, { sitting: PalaceKey; facing: PalaceKey }> = {
    north: { sitting: 'south', facing: 'north' },
    south: { sitting: 'north', facing: 'south' },
    east: { sitting: 'west', facing: 'east' },
    west: { sitting: 'east', facing: 'west' },
    unknown: { sitting: 'north', facing: 'south' },
  };

  return map[orientation] ?? map.unknown;
};

export const normalizeDegrees = (degrees: number) => ((degrees % 360) + 360) % 360;

export const getPalaceFromBearing = (bearing: number): PalaceKey => {
  const sectors: PalaceKey[] = [
    'north',
    'northeast',
    'east',
    'southeast',
    'south',
    'southwest',
    'west',
    'northwest',
  ];
  const index = Math.round(normalizeDegrees(bearing) / 45) % sectors.length;
  return sectors[index];
};

export const getSittingFacingFromCompass = (
  centerPoint: { x: number; y: number },
  entrancePoint: { x: number; y: number },
  compassRotation: number,
) => {
  const dx = entrancePoint.x - centerPoint.x;
  const dy = entrancePoint.y - centerPoint.y;
  const screenBearing = normalizeDegrees(Math.atan2(dx, -dy) * 180 / Math.PI);
  const compassZeroOffset = 180;
  const facingBearing = normalizeDegrees(screenBearing + compassZeroOffset - compassRotation);
  const sittingBearing = normalizeDegrees(facingBearing + 180);
  const facing = getPalaceFromBearing(facingBearing);
  const sittingMountain = getTwentyFourMountain(sittingBearing);
  const facingMountain = getTwentyFourMountain(facingBearing);
  const sittingLineInfo = getMountainLineInfo(sittingBearing);
  const facingLineInfo = getMountainLineInfo(facingBearing);

  return {
    sitting: oppositePalace[facing],
    facing,
    sittingMountain,
    facingMountain,
    sittingLineInfo,
    facingLineInfo,
    sittingBearing,
    facingBearing,
  };
};

export const buildHouseLuckChart = (
  houseYear: number,
  annualYear = new Date().getFullYear(),
  sitting: PalaceKey = 'north',
  facing: PalaceKey = oppositePalace[sitting],
  sittingAngle = 0,
  facingAngle = 180,
): HouseLuckChart => {
  const period = getHousePeriod(houseYear);
  const periodMap = buildStarMap(period, 'forward');
  const mountainCenterStar = periodMap.get(sitting) ?? period;
  const facingCenterStar = periodMap.get(facing) ?? period;
  const normalizedSittingAngle = normalizeDegrees(sittingAngle);
  const normalizedFacingAngle = normalizeDegrees(facingAngle);
  const sittingMountain = getTwentyFourMountain(normalizedSittingAngle);
  const facingMountain = getTwentyFourMountain(normalizedFacingAngle);
  const sittingMountainMeta = getMountainMeta(sittingMountain);
  const facingMountainMeta = getMountainMeta(facingMountain);
  const sittingLineInfo = getMountainLineInfo(normalizedSittingAngle);
  const facingLineInfo = getMountainLineInfo(normalizedFacingAngle);
  const annualCenterStar = getAnnualCenterStar(annualYear);
  const mountainDirection = getDirectionalFlight(sittingMountain);
  const facingDirection = getDirectionalFlight(facingMountain);
  const mountainMap = buildStarMap(mountainCenterStar, mountainDirection);
  const facingMap = buildStarMap(facingCenterStar, facingDirection);
  const annualMap = buildStarMap(annualCenterStar, 'reverse');

  return {
    houseYear,
    annualYear,
    period,
    periodText: chineseNumbers[period],
    periodRange: periodRanges[period],
    sitting,
    facing,
    sittingLabel: getPalaceLabel(sitting),
    facingLabel: getPalaceLabel(facing),
    sittingMountain,
    facingMountain,
    sittingMountainLabel: `${sittingMountain}山`,
    facingMountainLabel: `${facingMountain}向`,
    sittingMountainMeta,
    facingMountainMeta,
    sittingLineInfo,
    facingLineInfo,
    sittingAngle: normalizedSittingAngle,
    facingAngle: normalizedFacingAngle,
    periodCenterStar: period,
    mountainCenterStar,
    facingCenterStar,
    annualCenterStar,
    periodDirection: 'forward',
    mountainDirection,
    facingDirection,
    annualDirection: 'reverse',
    flightPath: forwardPath,
    cells: palaceGrid.map((palace) => {
      const periodStar = periodMap.get(palace.key) ?? period;
      const mountainStar = mountainMap.get(palace.key) ?? mountainCenterStar;
      const facingStar = facingMap.get(palace.key) ?? facingCenterStar;
      const annualStar = annualMap.get(palace.key) ?? annualCenterStar;

      return {
        key: palace.key,
        label: palace.label,
        trigram: palace.trigram,
        palaceElement: palace.element,
        periodStar,
        periodStarName: starNames[periodStar],
        mountainStar,
        mountainStarName: starNames[mountainStar],
        facingStar,
        facingStarName: starNames[facingStar],
        annualStar,
        annualStarName: starNames[annualStar],
        x: palace.x,
        y: palace.y,
      };
    }),
  };
};
